import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import { Formik, Form } from 'formik'
import moment from 'moment-timezone'

import conferenceSlice, {
  fetchConferenceById,
  updateConferenceById,
  addConference,
  resetConferenceStore
} from 'store/conference'
import { fetchAllOrganizers } from 'store/Organizers'
import { confFormSchema } from 'schemas/conference'
import ProgressButton from 'components/ProgressButton'
import CustomTextField from 'components/Form/CustomTextField'
import PasswordField from 'components/PasswordField'
import { Collection, Roles } from 'constants/index'
import CustomDateTimePicker from 'components/CustomDateTimePicker'
import { getDefaultTimezone } from 'utils/times'
import { useHistory, useParams } from 'react-router-dom'
import Autocomplete from '@material-ui/lab/Autocomplete'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import CustomSelect from 'components/Form/CustomSelect'
import { fetchSuperAdminConfiguration } from 'store/config'
import { updateSettingById } from 'store/settings'
import ErrorMessage from 'components/ErrorMessage'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'

import {
  Add,
  CheckCircle,
  Delete,
  Edit,
  ErrorOutline,
  InfoOutlined,
  Person
} from '@material-ui/icons'
import CustomPopup from 'components/CustomPopup'
import * as yup from 'yup'
import { cloudFunctionsInstance } from 'config/axios'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

const timeZonesOptions = moment.tz.names()

const ImgKeys = {
  confMap: 'confMap',
  confSplashScreenImg: 'confSplashScreenImg',
  boothMap: 'boothMap',
  confLogo: 'confLogo'
}

const CreationProgressDialog = ({ open, steps }) => (
  <Dialog open={open} disableEscapeKeyDown maxWidth="xs" fullWidth>
    <DialogTitle>Creating Conference</DialogTitle>
    <DialogContent>
      <Box style={{ padding: '8px 0 16px' }}>
        {steps.map((step, i) => (
          <Box
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: i < steps.length - 1 ? '20px' : '0'
            }}>
            <Box style={{ width: 24, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              {step.status === 'active' && <CircularProgress size={20} />}
              {step.status === 'done' && <CheckCircle style={{ color: '#4caf50', fontSize: 22 }} />}
              {step.status === 'error' && (
                <ErrorOutline style={{ color: '#f44336', fontSize: 22 }} />
              )}
              {step.status === 'pending' && (
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px solid #e0e0e0',
                    boxSizing: 'border-box'
                  }}
                />
              )}
            </Box>
            <Typography
              style={{
                color:
                  step.status === 'pending'
                    ? '#9e9e9e'
                    : step.status === 'error'
                    ? '#f44336'
                    : '#212121',
                fontWeight: step.status === 'active' ? 600 : 400,
                fontSize: '15px'
              }}>
              {step.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </DialogContent>
  </Dialog>
)

const ConferenceUsersSummaryDialog = ({ summary, onClose }) => {
  if (!summary) return null
  const { failed = [], assignedElsewhere = [] } = summary

  return (
    <Dialog open maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
      <DialogTitle style={{ paddingBottom: 4 }}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Conference Users Summary
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 8 }}>
        {failed.length > 0 && (
          <Box style={{ marginBottom: assignedElsewhere.length > 0 ? '28px' : '8px' }}>
            <Box
              style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <ErrorOutline style={{ color: '#c62828', fontSize: 20 }} />
              <Typography style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                {failed.length === 1
                  ? 'This user could not be added'
                  : `${failed.length} users could not be added`}
              </Typography>
            </Box>
            <Box style={{ border: '1px solid #fecaca', borderRadius: 8, overflow: 'hidden' }}>
              {failed.map((u, index) => (
                <Box
                  key={u.email}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: index % 2 === 0 ? '#fef2f2' : '#ffffff',
                    borderBottom: index < failed.length - 1 ? '1px solid #fecaca' : 'none'
                  }}>
                  <Typography style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                    {u.fullName ? `${u.fullName} (${u.email})` : u.email}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#b91c1c' }}>
                    {u.error}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {assignedElsewhere.length > 0 && (
          <Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <InfoOutlined style={{ color: '#b45309', fontSize: 20 }} />
              <Typography style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                {assignedElsewhere.length === 1
                  ? 'This user already belongs to another conference'
                  : `${assignedElsewhere.length} users already belong to other conferences`}
              </Typography>
            </Box>
            <Typography variant="body2" style={{ color: '#64748b', marginBottom: '14px' }}>
              They were also added to this conference — no action is needed.
            </Typography>
            {assignedElsewhere.map((u) => (
              <Box
                key={u.email}
                style={{
                  marginBottom: '14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}>
                <Box
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                  <Typography style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                    {u.fullName ? `${u.fullName} (${u.email})` : u.email}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#64748b' }}>
                    Already in {u.otherConferences.length} other active conference
                    {u.otherConferences.length > 1 ? 's' : ''}:
                  </Typography>
                </Box>
                {u.otherConferences.map((conf, index) => {
                  const isOwner = conf.role === 'owner'
                  return (
                    <Box
                      key={conf.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 14px',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom:
                          index < u.otherConferences.length - 1 ? '1px solid #f1f5f9' : 'none'
                      }}>
                      <Typography style={{ fontSize: 13, color: '#334155' }}>
                        {conf.confName}
                      </Typography>
                      <Chip
                        label={conf.role.charAt(0).toUpperCase() + conf.role.slice(1)}
                        size="small"
                        style={{
                          backgroundColor: isOwner ? '#dbeafe' : '#f1f5f9',
                          color: isOwner ? '#1d4ed8' : '#475569',
                          fontWeight: 600,
                          fontSize: 11,
                          height: 22
                        }}
                      />
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-end', padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          style={{ textTransform: 'none', borderRadius: 8, paddingLeft: 24, paddingRight: 24 }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ConferenceForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { conferenceId } = useParams()
  const { user } = useSelector((state) => state.auth)
  const { conference, conferences, inProgress } = useSelector((state) => state.conference)
  const { organizers } = useSelector((state) => state.organizers)

  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN
  const isOrganizer = user?.role === Roles.ORGANIZER
  const [loading, setLoading] = useState(false)
  const [progressDialog, setProgressDialog] = useState({ open: false, steps: [] })
  const [usersSummary, setUsersSummary] = useState(null)

  const previousConfs = useCallback(() => {
    if (conferenceId) {
      return conferences.filter((conf) => conf.id !== conferenceId)
    }
    return conferences
  }, [conferences, conferenceId])

  const [locationMapFile, setLocationMapFile] = useState(null)
  const [splashScreenFile, setSplashScreenFile] = useState(null)
  const [boothMapFile, setBoothMapFile] = useState(null)
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const defaultTimezone = useMemo(() => getDefaultTimezone(conference?.timezone), [conference])

  const initialValues = {
    confName: conference?.confName ?? '',
    confCode: conference?.confCode ?? '',
    timezone: conference?.timezone ?? '',
    startDate: conference?.startDate
      ? moment(conference?.startDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    endDate: conference?.endDate
      ? moment(conference?.endDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    viewableUntil:
      conference?.viewableUntil && conference?.timezone
        ? moment(conference?.viewableUntil).tz(conference?.timezone)
        : conference?.endDate && conference?.timezone
        ? moment(conference?.endDate).tz(conference?.timezone)
        : moment().tz(defaultTimezone),
    is24hrFormat: conference?.is24hrFormat ?? false,
    isActive: conference?.isActive ?? true,
    abstractPrefix: conference?.abstractPrefix ?? '',
    posterPrefix: conference?.posterPrefix ?? '',
    havePolls: conference?.havePolls ?? false,
    enableBadgeModule: conference?.enableBadgeModule ?? false,
    badgeQrCodeFormat: conference?.badgeQrCodeFormat || 'plain',
    hasPreviousConference: conference?.previousConferenceId ? 'yes' : 'no',
    previousConferenceId: conference?.previousConferenceId ?? '',
    // general settings
    speakerCanUploadTalks: conference?.speakerCanUploadTalks || false,
    enableAttendeeToChangeTimezone: conference?.enableAttendeeToChangeTimezone || false,
    enableResources: conference?.enableResources || false,
    enableAttendance: conference?.enableAttendance || false,
    attendanceStartTime: conference?.attendanceStartTime
      ? moment(conference?.attendanceStartTime, 'hh:mm A')
      : moment('08:00 AM', 'hh:mm A'),
    attendanceEndTime: conference?.attendanceEndTime
      ? moment(conference?.attendanceEndTime, 'hh:mm A')
      : moment('08:00 PM', 'hh:mm A'),
    attendanceInfoMessage: conference?.attendanceInfoMessage || '',
    defaultGraceTime:
      conference?.defaultGraceTime !== undefined && conference?.defaultGraceTime !== null
        ? conference.defaultGraceTime
        : 15,
    resourceContent: conference?.resourceContent || '',
    users: conference?.users || [],
    organizerId: conference?.organizerId ?? null
  }

  useEffect(() => {
    if (conferenceId) {
      dispatch(fetchConferenceById(conferenceId))
    }
    if (isSuperAdmin) {
      dispatch(fetchAllOrganizers())
    }
    return () => {
      if (isSuperAdmin) {
        dispatch(conferenceSlice.actions.setConference(null))
      }
    }
  }, [dispatch, conferenceId, isSuperAdmin])

  const handleFilesChange = (key) => (file) => {
    if (key === ImgKeys.confMap) {
      setLocationMapFile(file)
    } else if (key === ImgKeys.confSplashScreenImg) {
      setSplashScreenFile(file)
    } else {
      setBoothMapFile(file)
    }
  }

  const handleUpload = (key) => async (name) => {
    const modifiedConf = { ...conference }
    delete modifiedConf['id']
    if (key === ImgKeys.confMap) {
      modifiedConf[ImgKeys.confMap] = name
    } else if (key === ImgKeys.confSplashScreenImg) {
      modifiedConf[ImgKeys.confSplashScreenImg] = name
    } else {
      modifiedConf[ImgKeys.boothMap] = name
    }
    await dispatch(updateConferenceById(conference?.id, modifiedConf))
  }

  const handleDelete = async (key) => {
    const modifiedConf = { ...conference }
    delete modifiedConf['id']
    if (key === ImgKeys.confMap) {
      modifiedConf[ImgKeys.confMap] = null
    } else if (key === ImgKeys.confSplashScreenImg) {
      modifiedConf[ImgKeys.confSplashScreenImg] = null
    } else {
      modifiedConf[ImgKeys.boothMap] = null
    }
    await dispatch(updateConferenceById(conference?.id, modifiedConf))
  }

  const goBack = () => {
    dispatch(resetConferenceStore())
    history.goBack()
  }

  const onSubmit = async ({
    startDate,
    endDate,
    viewableUntil,
    confCode,
    timezone,
    confName,
    is24hrFormat,
    isActive,
    abstractPrefix,
    posterPrefix,
    havePolls,
    enableBadgeModule,
    badgeQrCodeFormat,
    hasPreviousConference,
    previousConferenceId,
    // general settings
    speakerCanUploadTalks,
    enableResources,
    enableAttendance,
    enableAttendeeToChangeTimezone,
    attendanceStartTime,
    attendanceEndTime,
    attendanceInfoMessage,
    defaultGraceTime,
    resourceContent,
    users,
    organizerId
  }) => {
    const resolvedOrganizerId = isOrganizer ? user.id : isSuperAdmin ? organizerId || null : null

    const payload = {
      havePolls: havePolls,
      startDate: typeof startDate === 'number' ? startDate : startDate.startOf('day').valueOf(),
      endDate: typeof endDate === 'number' ? endDate : endDate.endOf('day').valueOf(),
      viewableUntil:
        typeof viewableUntil === 'number' ? viewableUntil : viewableUntil.endOf('day').valueOf(),
      confCode: confCode.trim(),
      timezone: timezone.trim(),
      confName: confName.trim(),
      is24hrFormat,
      isActive,
      abstractPrefix: abstractPrefix.toUpperCase(),
      posterPrefix: posterPrefix.toUpperCase(),
      enableBadgeModule,
      badgeQrCodeFormat,
      previousConferenceId: hasPreviousConference === 'yes' ? previousConferenceId : '',
      // general settings
      speakerCanUploadTalks: speakerCanUploadTalks,
      enableResources: enableResources,
      enableAttendance: enableAttendance,
      enableAttendeeToChangeTimezone: enableAttendeeToChangeTimezone,
      attendanceStartTime: enableAttendance ? moment(attendanceStartTime).format('hh:mm A') : null,
      attendanceEndTime: enableAttendance ? moment(attendanceEndTime).format('hh:mm A') : null,
      attendanceInfoMessage: enableAttendance ? attendanceInfoMessage?.trim() : '',
      defaultGraceTime: enableAttendance ? Number(defaultGraceTime) : null,
      resourceContent: enableResources ? resourceContent : '',
      organizerId: resolvedOrganizerId
    }

    if (conferenceId) {
      const otherConferences = conferences.filter((row) => row.id !== conferenceId)
      const sameConf = otherConferences.find((row) => row.confCode === payload.confCode)
      if (sameConf) {
        toast.warning('A conference with this code already exists.')
      } else {
        setLoading(true)
        await dispatch(updateConferenceById(conferenceId, payload))
        if (user.role === Roles.ADMIN) {
          dispatch(fetchConferenceById(conferenceId))
        }
        toast.success('Conference updated.')
        setLoading(false)
      }
    } else {
      const sameConf = conferences.find((row) => row.confCode === payload.confCode)
      if (sameConf) {
        toast.warning('A conference with this code already exists.')
        return
      }

      const hasImages = !!(locationMapFile || splashScreenFile || boothMapFile)
      const hasUsers = !!(users && users.length > 0)

      const stepLabels = [
        'Creating conference...',
        'Copying default email templates...',
        ...(hasImages ? ['Uploading images...'] : []),
        ...(hasUsers ? ['Creating conference users...'] : [])
      ]

      let stepIdx = 0
      setProgressDialog({
        open: true,
        steps: stepLabels.map((label, i) => ({ label, status: i === 0 ? 'active' : 'pending' }))
      })
      setLoading(true)

      const markDone = (idx) => {
        setProgressDialog((prev) => ({
          ...prev,
          steps: prev.steps.map((s, i) => {
            if (i === idx) return { ...s, status: 'done' }
            if (i === idx + 1) return { ...s, status: 'active' }
            return s
          })
        }))
      }

      const markError = (idx) => {
        setProgressDialog((prev) => ({
          ...prev,
          steps: prev.steps.map((s, i) => (i === idx ? { ...s, status: 'error' } : s))
        }))
      }

      let pendingUsersSummary = null

      try {
        const confId = await dispatch(addConference(payload))
        markDone(stepIdx++)

        const configuration = await fetchSuperAdminConfiguration()
        await dispatch(
          updateSettingById(
            confId,
            { ...configuration?.emailTemplates },
            () => {},
            () => {}
          )
        )
        markDone(stepIdx++)

        if (hasImages) {
          if (locationMapFile) {
            payload.confMap = await uploadImageFirstTime(
              locationMapFile,
              Collection.CONFERENCES,
              confId
            )
          }
          if (splashScreenFile) {
            payload.confSplashScreenImg = await uploadImageFirstTime(
              splashScreenFile,
              Collection.CONFERENCES,
              confId
            )
          }
          if (boothMapFile) {
            payload.boothMap = await uploadImageFirstTime(
              boothMapFile,
              Collection.CONFERENCES,
              confId
            )
          }
          await dispatch(updateConferenceById(confId, payload))
          markDone(stepIdx++)
        }

        if (hasUsers) {
          try {
            const usersPayload = {
              conferenceId: confId,
              users: users.map((user) => ({
                fullName: user.fullName.trim(),
                email: user.email.trim(),
                role: user.role,
                password: user.password
              }))
            }
            const response = await cloudFunctionsInstance.post(
              '/createConferenceUsers',
              usersPayload
            )
            markDone(stepIdx++)

            const results = response.data?.results || []
            const failed = results
              .map((r, i) => ({ ...r, fullName: users[i]?.fullName }))
              .filter((r) => r.status === 'failed')
            const assignedElsewhere = results
              .map((r, i) => ({ ...r, fullName: users[i]?.fullName }))
              .filter((r) => r.status === 'assigned' && r.otherConferences?.length > 0)

            if (failed.length > 0 || assignedElsewhere.length > 0) {
              pendingUsersSummary = { failed, assignedElsewhere }
            }

            if (failed.length > 0 && failed.length === results.length) {
              toast.error('Conference created, but none of the conference users could be added.')
            } else {
              toast.success(
                `${
                  response.data.msg ||
                  'Conference users created and invited via email successfully.'
                }`
              )
            }
          } catch (err) {
            markError(stepIdx++)
            toast.error(
              'Conference created, but user accounts could not be set up. Add them manually.'
            )
            console.error('Error creating conference users:', err)
          }
        }

        toast.success('Conference created.')
      } catch (err) {
        markError(stepIdx)
      } finally {
        setLoading(false)
        setTimeout(() => {
          setProgressDialog({ open: false, steps: [] })
          if (pendingUsersSummary) {
            setUsersSummary(pendingUsersSummary)
          } else if (isSuperAdmin || isOrganizer) {
            goBack()
          }
        }, 1500)
      }
      return
    }
    if (isSuperAdmin || isOrganizer) {
      goBack()
    }
  }

  // User management functions
  const MAX_CONFERENCE_USERS = 5

  const handleAddUser = (userData, setFieldValue, currentUsers) => {
    if (currentUsers.length >= MAX_CONFERENCE_USERS) {
      toast.error(`Maximum of ${MAX_CONFERENCE_USERS} users allowed per conference`)
      return
    }
    const updatedUsers = [...currentUsers, { ...userData, id: Date.now() }]
    setFieldValue('users', updatedUsers)
    toast.success('User added.')
  }

  const handleEditUser = (userData, setFieldValue, currentUsers, userToEdit) => {
    const userIndex = currentUsers.findIndex((u) => u === userToEdit)
    if (userIndex !== -1) {
      const updatedUsers = [...currentUsers]
      updatedUsers[userIndex] = { ...userData, id: userToEdit.id || Date.now() }
      setFieldValue('users', updatedUsers)
      toast.success('User updated.')
    }
  }

  // UserManagementModal Component
  const UserManagementModal = ({ open, onClose, user, onSave, users }) => {
    const initialValues = {
      fullName: user?.fullName || '',
      email: user?.email || '',
      role: user?.role || 'admin',
      password: user?.password || ''
    }

    const validationSchema = yup.object().shape({
      fullName: yup.string().trimmedRequired('Full name is required'),
      email: yup
        .string()
        .email('Invalid email format')
        .required('Email is required')
        .test('unique-email', 'This email is already used by another user', function (value) {
          if (!value) return true
          const existingUser = users.find(
            (u) => u.email.toLowerCase() === value.toLowerCase() && u !== user
          )
          return !existingUser
        }),
      role: yup
        .string()
        .oneOf(['admin', 'owner'], 'Role must be admin or owner')
        .required('Role is required')
        .test('unique-owner', 'Only one owner is allowed per conference', function (value) {
          if (value !== 'owner') return true
          const existingOwner = users.find((u) => u.role === 'owner' && u !== user)
          return !existingOwner
        }),
      password: yup
        .string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required')
    })

    const handleSubmit = (values) => {
      onSave(values)
      onClose()
    }

    return (
      <CustomPopup
        open={open}
        title={user ? 'Edit Conference User' : 'Add Conference User'}
        onClose={onClose}
        maxWidth="md">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={3} style={{ padding: '16px 0' }}>
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Full Name"
                    name="fullName"
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.fullName && errors.fullName)}
                    helperText={touched.fullName && errors.fullName}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomSelect
                    inputLabel="Role"
                    options={[
                      { id: 'admin', label: 'Admin' },
                      { id: 'owner', label: 'Owner' }
                    ]}
                    optionAttr="label"
                    name="role"
                  />
                </Grid>

                <Grid item xs={12}>
                  <PasswordField
                    name="password"
                    label="Password"
                    autoComplete="new-password"
                    formik={{
                      values,
                      handleChange,
                      handleBlur,
                      touched,
                      errors
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <Button onClick={onClose} variant="outlined">
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                      {user ? 'Update' : 'Add'} User
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </CustomPopup>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.pageTitle}>
          {conferenceId ? 'Edit Conference' : 'Create Conference'}
        </Typography>
        {(isSuperAdmin || isOrganizer) && (
          <Button variant="outlined" onClick={goBack}>
            Back
          </Button>
        )}
      </Box>

      {conferenceId && inProgress ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            gap: '1rem'
          }}>
          <CircularProgress />
          Loading, Please wait...
        </Box>
      ) : (
        <Formik
          enableReinitialize={!!conferenceId}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={confFormSchema}>
          {({ setFieldValue, setFieldTouched, validateForm, values, errors, touched }) => (
            <Form>
              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={7}>
                  <Paper className={classes.section}>
                    <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
                      Basic Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CustomTextField label="Conference Name" name="confName" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomTextField label="Conference Code" name="confCode" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          disableClearable
                          options={timeZonesOptions}
                          getOptionLabel={(option) => option}
                          value={values.timezone}
                          onChange={(event, value) => {
                            if (value) {
                              setFieldValue('timezone', value)
                              setFieldValue('startDate', moment(values.startDate).tz(value))
                              setFieldValue('endDate', moment(values.endDate).tz(value))
                              setFieldValue('viewableUntil', moment(values.viewableUntil).tz(value))
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="timezone"
                              error={!!(touched.timezone && errors.timezone)}
                              helperText={
                                touched.timezone && errors.timezone ? errors.timezone : ''
                              }
                              label="Timezone"
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="Abstract Number Prefix"
                          name="abstractPrefix"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="Poster Number Prefix"
                          name="posterPrefix"
                          fullWidth
                        />
                      </Grid>

                      {isSuperAdmin && (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={[
                              { uid: null, fullName: '— None (Super Admin managed) —' },
                              ...organizers
                            ]}
                            getOptionLabel={(option) => option.fullName || option.email || ''}
                            getOptionSelected={(option, value) => option.uid === value?.uid}
                            value={organizers.find((o) => o.uid === values.organizerId) || null}
                            onChange={(_, selected) =>
                              setFieldValue('organizerId', selected?.uid ?? null)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Assign to Organizer (optional)"
                                variant="outlined"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} style={{ paddingTop: '0', paddingBottom: '0' }}>
                        <Typography
                          variant="subtitle1"
                          color="secondary"
                          className={classes.subheading}>
                          Conference Dates
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomDateTimePicker
                          mode="date"
                          value={values.startDate}
                          label="Start Date"
                          name="startDate"
                          disablePast
                          minDate={moment().tz(defaultTimezone)}
                          onChange={(value) => {
                            setTimeout(() => {
                              setFieldTouched('startDate', true, false)
                              setFieldValue('startDate', value, false)
                              // Trigger validation manually after update
                              validateForm()
                            }, 0)
                          }}
                          error={!!(touched.startDate && errors.startDate)}
                          helperText={touched.startDate && errors.startDate ? errors.startDate : ''}
                          onBlur={() => setFieldTouched('startDate', true)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomDateTimePicker
                          mode="date"
                          value={values.endDate}
                          label="End Date"
                          name="endDate"
                          disablePast
                          minDate={values.startDate}
                          onChange={(value) => {
                            // Use setTimeout to batch the updates and then validate
                            setTimeout(() => {
                              setFieldTouched('endDate', true, false)
                              setFieldValue('endDate', value, false)
                              setFieldValue('viewableUntil', value, false)
                              // Trigger validation manually after all updates
                              validateForm()
                            }, 0)
                          }}
                          error={!!(touched.endDate && errors.endDate)}
                          helperText={touched.endDate && errors.endDate ? errors.endDate : ''}
                          onBlur={() => setFieldTouched('endDate', true)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomDateTimePicker
                          mode="date"
                          value={values.viewableUntil}
                          label="Viewable Until"
                          name="viewableUntil"
                          disablePast
                          minDate={values.endDate}
                          onChange={(value) => {
                            setTimeout(() => {
                              setFieldTouched('viewableUntil', true, false)
                              setFieldValue('viewableUntil', value, false)
                              // Trigger validation manually after update
                              validateForm()
                            }, 0)
                          }}
                          error={!!(touched.viewableUntil && errors.viewableUntil)}
                          helperText={
                            touched.viewableUntil && errors.viewableUntil
                              ? errors.viewableUntil
                              : ''
                          }
                          onBlur={() => setFieldTouched('viewableUntil', true)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="is24hrFormat"
                              onChange={(e) => setFieldValue('is24hrFormat', e.target.checked)}
                              checked={values.is24hrFormat}
                              color="primary"
                            />
                          }
                          label="Use 24hr Format"
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper className={classes.section}>
                    <Box
                      className={classes.sectionTitle}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                      <Typography variant="h6" color="secondary" style={{ fontWeight: 'bold' }}>
                        Resources
                      </Typography>

                      <Switch
                        color="primary"
                        name="enableResources"
                        onChange={(e) => setFieldValue('enableResources', e.target.checked)}
                        checked={values.enableResources}
                      />
                    </Box>

                    {values.enableResources ? (
                      <Box className={classes.resourceContainer}>
                        <label className={classes.label}>Resource Content</label>
                        <TipTapEditor
                          hasPlaceholders={false}
                          value={values.resourceContent}
                          onChange={(value) => setFieldValue('resourceContent', value)}
                          placeholder="Write resource content here..."
                        />
                        <ErrorMessage
                          message={touched?.resourceContent && errors?.resourceContent}
                        />
                      </Box>
                    ) : (
                      <Typography style={{ textAlign: 'center' }}>
                        This module is currently disabled. Toggle the switch to enable it.
                      </Typography>
                    )}
                  </Paper>

                  <Paper className={classes.section}>
                    <Box
                      className={classes.sectionTitle}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                      <Typography variant="h6" color="secondary" style={{ fontWeight: 'bold' }}>
                        Attendance
                      </Typography>
                      <Switch
                        color="primary"
                        name="enableAttendance"
                        onChange={(e) => setFieldValue('enableAttendance', e.target.checked)}
                        checked={values.enableAttendance}
                      />
                    </Box>

                    {values.enableAttendance ? (
                      <>
                        <Typography variant="subtitle2" className={classes.label}>
                          Attendance Check-In Time slot
                        </Typography>
                        <Typography variant="body2" className={classes.subText}>
                          Set a specific time period for attendees to mark their attendance to
                          receive educational credits
                        </Typography>

                        <Grid container spacing={2} className={classes.timePickerContainer}>
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <TimePicker
                                label="Start Time"
                                value={values.attendanceStartTime}
                                onChange={(newValue) => {
                                  setFieldValue('attendanceStartTime', newValue)
                                }}
                                name="attendanceStartTime"
                                slotProps={{
                                  textField: {
                                    readOnly: true,
                                    fullWidth: true,
                                    placeholder: `hh:mm ${!conference?.is24hrFormat ? 'A' : ''}`,
                                    error: !!(
                                      touched.attendanceStartTime && errors.attendanceStartTime
                                    ),
                                    helperText:
                                      touched.attendanceStartTime && errors.attendanceStartTime
                                        ? errors.attendanceStartTime
                                        : ''
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <TimePicker
                                label="End Time"
                                value={values.attendanceEndTime}
                                onChange={(newValue) => {
                                  setFieldValue('attendanceEndTime', newValue)
                                }}
                                name="attendanceEndTime"
                                slotProps={{
                                  textField: {
                                    readOnly: true,
                                    fullWidth: true,
                                    placeholder: `hh:mm ${!conference?.is24hrFormat ? 'A' : ''}`,
                                    error: !!(
                                      touched.attendanceEndTime && errors.attendanceEndTime
                                    ),
                                    helperText:
                                      touched.attendanceEndTime && errors.attendanceEndTime
                                        ? errors.attendanceEndTime
                                        : ''
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>

                        <Box className={classes.messageContainer}>
                          <Typography variant="subtitle2" className={classes.label}>
                            Grace Time (minutes)
                          </Typography>
                          <Typography variant="body2" className={classes.subText}>
                            Admin-configured window, before and after a target&apos;s start time
                            within which a scan auto-approves. Configured once per Conference and
                            applied globally to every Event/Session.
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <CustomTextField
                                fullWidth
                                type="number"
                                label="Grace Time (minutes)"
                                name="defaultGraceTime"
                                inputProps={{ min: 0, step: 1 }}
                              />
                            </Grid>
                          </Grid>
                          <Typography variant="body2" className={classes.noteText}>
                            Scans outside the grace time window are still recorded as attendance,
                            but must be manually approved or rejected by an admin.
                          </Typography>
                        </Box>

                        <Box className={classes.messageContainer}>
                          <Typography variant="subtitle2" className={classes.label}>
                            Attendance Explanation Message
                          </Typography>
                          <Typography variant="body2" className={classes.subText}>
                            This text appears as helpful guidelines for attendees when marking their
                            attendance
                          </Typography>
                          <TipTapEditor
                            hasPlaceholders={false}
                            value={values.attendanceInfoMessage}
                            onChange={(value) => setFieldValue('attendanceInfoMessage', value)}
                            placeholder="Write attendance info message here..."
                          />
                          <ErrorMessage
                            message={
                              touched?.attendanceInfoMessage && errors?.attendanceInfoMessage
                            }
                          />
                        </Box>
                      </>
                    ) : (
                      <Typography style={{ textAlign: 'center' }}>
                        This module is currently disabled. Toggle the switch to enable it.
                      </Typography>
                    )}
                  </Paper>

                  <Paper className={classes.section}>
                    {(isSuperAdmin || isOrganizer) && !conferenceId && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box className={classes.usersHeader}>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                color="secondary"
                                className={classes.subheading}>
                                Conference Users ({values.users?.length || 0}/5)
                              </Typography>
                              <Typography variant="body2" className={classes.subText}>
                                Add users who will have administrative access to this conference
                                (Maximum 5 users allowed)
                              </Typography>
                            </Box>
                            <Button
                              startIcon={<Add />}
                              variant="contained"
                              color="primary"
                              size="small"
                              disabled={(values.users?.length || 0) >= 5}
                              onClick={() => {
                                setCurrentUser(null)
                                setUserModalOpen(true)
                              }}>
                              Add User
                            </Button>
                          </Box>

                          <Box className={classes.usersContainer}>
                            {values.users && values.users.length > 0 ? (
                              <List className={classes.usersList}>
                                {values.users.map((user, index) => (
                                  <ListItem key={index} className={classes.userListItem}>
                                    <ListItemIcon>
                                      <Person
                                        color={user.role === 'owner' ? 'primary' : 'action'}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={user.fullName}
                                      secondary={
                                        <Box>
                                          <Typography variant="body2" component="span">
                                            {user.email}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            component="span"
                                            className={classes.roleChip}
                                            style={{
                                              backgroundColor:
                                                user.role === 'owner' ? '#1976d2' : '#757575',
                                              color: 'white',
                                              padding: '2px 8px',
                                              borderRadius: '12px',
                                              marginLeft: '8px',
                                              fontSize: '10px'
                                            }}>
                                            {user.role.toUpperCase()}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => {
                                          setCurrentUser(user)
                                          setUserModalOpen(true)
                                        }}>
                                        <Edit />
                                      </IconButton>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        style={{ marginLeft: '6px' }}
                                        onClick={() => {
                                          const updatedUsers = values.users.filter(
                                            (_, i) => i !== index
                                          )
                                          setFieldValue('users', updatedUsers)
                                        }}>
                                        <Delete />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" className={classes.emptyState}>
                                No users added yet. Click &quot;Add User&quot; to get started (up to
                                5 users allowed).
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={5}>
                  <Paper className={classes.section}>
                    <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
                      Feature Toggles
                    </Typography>

                    <Grid container spacing={2}>
                      {/*Todo: Hiding this item as for now, its not needed.*/}
                      {/*<Grid item xs={12} md={6}>*/}
                      {/*  <Box className={classes.toggleItem}>*/}
                      {/*    <Typography className={classes.toggleLabel}>*/}
                      {/*      Allow speakers to upload their talks*/}
                      {/*    </Typography>*/}
                      {/*    <Switch*/}
                      {/*      color="primary"*/}
                      {/*      name="speakerCanUploadTalks"*/}
                      {/*      onChange={(e) =>*/}
                      {/*        setFieldValue('speakerCanUploadTalks', e.target.checked)*/}
                      {/*      }*/}
                      {/*      checked={values.speakerCanUploadTalks}*/}
                      {/*    />*/}
                      {/*  </Box>*/}
                      {/*</Grid>*/}

                      <Grid item xs={12} md={6}>
                        <Box className={classes.toggleItem}>
                          <Typography className={classes.toggleLabel}>
                            Allow attendees to change timezone
                          </Typography>
                          <Switch
                            color="primary"
                            name="enableAttendeeToChangeTimezone"
                            onChange={(e) =>
                              setFieldValue('enableAttendeeToChangeTimezone', e.target.checked)
                            }
                            checked={values.enableAttendeeToChangeTimezone}
                          />
                        </Box>
                      </Grid>

                      {(isSuperAdmin || isOrganizer) && (
                        <>
                          <Grid item xs={12} md={6}>
                            <Box className={classes.toggleItem}>
                              <Typography className={classes.toggleLabel}>
                                Enable polling module
                              </Typography>
                              <Switch
                                color="primary"
                                name="havePolls"
                                onChange={(e) => setFieldValue('havePolls', e.target.checked)}
                                checked={values.havePolls}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box className={classes.toggleItem}>
                              <Typography className={classes.toggleLabel}>
                                Enable badge module
                              </Typography>
                              <Switch
                                color="primary"
                                name="enableBadgeModule"
                                onChange={(e) =>
                                  setFieldValue('enableBadgeModule', e.target.checked)
                                }
                                checked={values.enableBadgeModule}
                              />
                            </Box>
                          </Grid>

                          {values.enableBadgeModule && (
                            <Grid item xs={12}>
                              <Box className={classes.radioGroup}>
                                <Typography className={classes.radioGroupLabel}>
                                  Badge QR Code Format
                                </Typography>
                                <RadioGroup
                                  row
                                  value={values.badgeQrCodeFormat}
                                  onChange={(e) =>
                                    setFieldValue('badgeQrCodeFormat', e.target.value)
                                  }>
                                  <FormControlLabel
                                    control={<Radio size="small" color="primary" />}
                                    label="Plain text"
                                    value="plain"
                                  />
                                  <FormControlLabel
                                    control={<Radio size="small" color="primary" />}
                                    label="vCard (scannable contact card)"
                                    value="vcard"
                                  />
                                </RadioGroup>
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12} md={6}>
                            <Box className={classes.toggleItem}>
                              <Typography className={classes.toggleLabel}>Is Active</Typography>
                              <Switch
                                color="primary"
                                name="isActive"
                                onChange={(e) => setFieldValue('isActive', e.target.checked)}
                                checked={values.isActive}
                              />
                            </Box>
                          </Grid>
                        </>
                      )}
                    </Grid>

                    {(isSuperAdmin || isOrganizer) && (
                      <>
                        <Divider className={classes.divider} />
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              color="secondary"
                              className={classes.subheading}>
                              Previous Conference Link
                            </Typography>
                            <Box className={classes.radioGroup}>
                              <Typography className={classes.radioGroupLabel}>
                                Do you want to link this conference to a previous one?
                              </Typography>
                              <RadioGroup
                                row
                                value={values.hasPreviousConference}
                                onChange={(e) =>
                                  setFieldValue('hasPreviousConference', e.target.value)
                                }>
                                <FormControlLabel
                                  control={<Radio size="small" color="primary" />}
                                  label="Yes"
                                  value="yes"
                                />
                                <FormControlLabel
                                  control={<Radio size="small" color="primary" />}
                                  label="No"
                                  value="no"
                                />
                              </RadioGroup>
                            </Box>
                          </Grid>

                          {values.hasPreviousConference === 'yes' && (
                            <Grid item xs={12}>
                              <CustomSelect
                                inputLabel="Previous Conference"
                                options={previousConfs()}
                                optionAttr="confName"
                                name="previousConferenceId"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}
                  </Paper>

                  <Paper className={classes.section}>
                    <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
                      Conference Images
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <AvatarPicker
                          title="Location Map"
                          subTitle="Allowed Format: PNG/JPG"
                          path={Collection.CONFERENCES}
                          handleCustomChange={(file) => handleFilesChange(ImgKeys.confMap)(file)}
                          onUpload={(name) => handleUpload(ImgKeys.confMap)(name)}
                          onDelete={() => handleDelete(ImgKeys.confMap)}
                          imageName={conference?.id === conferenceId ? conference?.confMap : null}
                          conferenceId={conference?.id === conferenceId ? conferenceId : null}
                          firsTimeUpload={!conferenceId}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AvatarPicker
                          title="Splash Screen"
                          subTitle="Allowed Format: PNG/JPG"
                          path={Collection.CONFERENCES}
                          handleCustomChange={(file) =>
                            handleFilesChange(ImgKeys.confSplashScreenImg)(file)
                          }
                          onUpload={(name) => handleUpload(ImgKeys.confSplashScreenImg)(name)}
                          onDelete={() => handleDelete(ImgKeys.confSplashScreenImg)}
                          imageName={
                            conference?.id === conferenceId ? conference?.confSplashScreenImg : null
                          }
                          conferenceId={conference?.id === conferenceId ? conferenceId : null}
                          firsTimeUpload={!conferenceId}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AvatarPicker
                          title="Booth Map"
                          subTitle="Allowed Format: PNG/JPG"
                          path={Collection.CONFERENCES}
                          handleCustomChange={(file) => handleFilesChange(ImgKeys.boothMap)(file)}
                          onUpload={(name) => handleUpload(ImgKeys.boothMap)(name)}
                          onDelete={() => handleDelete(ImgKeys.boothMap)}
                          imageName={conference?.id === conferenceId ? conference?.boothMap : null}
                          conferenceId={conference?.id === conferenceId ? conferenceId : null}
                          firsTimeUpload={!conferenceId}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              <Box className={classes.actions}>
                {(isSuperAdmin || isOrganizer) && (
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={loading}
                    onClick={goBack}
                    className={classes.cancelButton}>
                    Cancel
                  </Button>
                )}
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                  color="primary"
                  variant="contained"
                  size="large"
                />
              </Box>

              {/* User Management Modal */}
              <UserManagementModal
                open={userModalOpen}
                onClose={() => {
                  setUserModalOpen(false)
                  setCurrentUser(null)
                }}
                user={currentUser}
                users={values?.users || []}
                onSave={(userData) => {
                  if (currentUser) {
                    handleEditUser(userData, setFieldValue, values?.users || [], currentUser)
                  } else {
                    handleAddUser(userData, setFieldValue, values?.users || [])
                  }
                }}
              />
            </Form>
          )}
        </Formik>
      )}

      <CreationProgressDialog open={progressDialog.open} steps={progressDialog.steps} />
      <ConferenceUsersSummaryDialog
        summary={usersSummary}
        onClose={() => {
          setUsersSummary(null)
          if (isSuperAdmin || isOrganizer) goBack()
        }}
      />
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    width: '100%',
    padding: '0',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0'
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '26px'
  },
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  subheading: {
    fontWeight: 600
  },
  divider: {
    margin: '16px 0'
  },
  toggleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0'
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 500
  },
  radioGroup: {
    marginTop: '8px',
    marginBottom: '16px'
  },
  radioGroupLabel: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    display: 'block'
  },
  timePickerContainer: {
    marginTop: '16px',
    marginBottom: '16px'
  },
  messageContainer: {
    marginTop: '24px'
  },
  resourceContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  quillEditor: {
    marginBottom: '16px'
  },
  label: {
    fontSize: '15px',
    fontWeight: 500,
    marginBottom: '8px'
  },
  subText: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '12px'
  },
  noteText: {
    fontSize: '13px',
    color: '#8a6d3b',
    marginTop: '8px',
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '24px',
    marginBottom: '36px'
  },
  cancelButton: {
    minWidth: '100px'
  },
  timePicker: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    }
  },
  usersContainer: {
    marginTop: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px'
  },
  usersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  usersHeaderText: {
    fontWeight: 500,
    color: '#666'
  },
  usersList: {
    padding: '0'
  },
  userListItem: {
    border: '1px solid #f0f0f0',
    borderRadius: '8px',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0
    }
  },
  roleChip: {
    display: 'inline-block'
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic',
    padding: '24px'
  }
})

export default ConferenceForm
